import type { FilterSearchListItem } from '@modules/search/components/filter-drawer/FilterSearchList';
import type { FilterStatesKey } from '@modules/search/type/filter-state';
import CheckboxTree from 'react-checkbox-tree';
import React from 'react';
import { useStyles, IndicatorCheckbox } from '@archipro-design/aria';
import * as S from './AwardsFilterTree.style';
import { AngleDown, AngleUp, SubtractLine } from '@archipro-design/icons';

export interface AwardsFilter {
    kind: 'AwardsFilter';
    filterKey: FilterStatesKey;
    subFilterKey?: string;
}

export interface AwardFilterState {
    kind: 'AwardsFilter';
    items: AwardCheckboxTreeNode[];
    checkedItems?: AwardFilterOption[];
    flatLeafValues: string[];
}

type AwardUpdateState = Pick<AwardFilterState, 'items' | 'checkedItems'> &
    Pick<AwardsFilter, 'kind'>;

export interface AwardFilterOption extends FilterSearchListItem {
    name?: string;
    institutionName?: string;
    institutionValue?: string;
}

export interface AwardCheckboxTreeNode extends AwardFilterOption {
    children?: AwardCheckboxTreeNode[];
}

interface AwardsFilterTreeProps extends AwardFilterState {
    filter: AwardsFilter;
    disabled?: boolean;
    updateFilter: (
        filterKey: FilterStatesKey,
        state: Partial<AwardUpdateState>
    ) => void;
}

const AwardsFilterTree = ({
    filter,
    disabled,
    updateFilter,
    items,
    checkedItems,
    flatLeafValues,
}: AwardsFilterTreeProps) => {
    const { css } = useStyles();

    const [checkedValues, setCheckedValues] = React.useState<string[]>(
        checkedItems ? checkedItems.map((c) => c.value) : []
    );
    const [expandedValues, setExpandedValues] = React.useState<string[]>([]);

    const handleFilterUpdate = (checked: string[]) => {
        const update: AwardFilterOption[] = [];

        const traverse = (node: AwardCheckboxTreeNode) => {
            if (checked.includes(node.value)) {
                update.push({
                    label: node.label,
                    value: node.value,
                    checked: true,
                });
            } else if (node.children && node.children.length > 0) {
                node.children.forEach((c) => traverse(c));
            }
        };

        items.forEach((n) => traverse(n));

        updateFilter &&
            updateFilter(filter.filterKey, {
                checkedItems: update,
                kind: filter.kind,
            });
    };

    const flatValues = flatTreeValues(items);
    const allValuesExcludingAll = flatValues.filter((v) => v !== 'all');

    const includeOrExcludeAllOption = (
        checkedItems: string[],
        value: string,
        allValues: string[]
    ): string[] => {
        const currentlyChecked = checkedValues.includes(value);
        if (value === 'all') {
            return currentlyChecked ? [] : flatValues;
        }

        return currentlyChecked
            ? checkedItems.filter((v) => v !== 'all')
            : allValues.every((v) => checkedItems.includes(v))
            ? ['all', ...checkedItems]
            : checkedItems;
    };

    return (
        <div className={css(S.AwardsFilterTree)}>
            <CheckboxTree
                disabled={disabled}
                nodes={items}
                checked={checkedValues}
                onCheck={(checked, node) => {
                    const checkedValues = includeOrExcludeAllOption(
                        checked,
                        node.value,
                        allValuesExcludingAll
                    );
                    setCheckedValues(checkedValues);
                    handleFilterUpdate(checkedValues);
                }}
                expanded={expandedValues}
                onExpand={(expanded) => {
                    setExpandedValues(expanded);
                }}
                onClick={(node) => {
                    if (node.children && node.children.length > 0) {
                        setExpandedValues(
                            expandedValues.includes(node.value)
                                ? expandedValues.filter((v) => v !== node.value)
                                : [...expandedValues, node.value]
                        );
                    } else {
                        const checked = checkedValues.includes(node.value)
                            ? checkedValues.filter((v) => v !== node.value)
                            : [...checkedValues, node.value];

                        const checkedWithAll = includeOrExcludeAllOption(
                            checked,
                            node.value,
                            flatLeafValues
                        );
                        setCheckedValues(checkedWithAll);
                        handleFilterUpdate(checkedWithAll);
                    }
                }}
                expandOnClick={true}
                showNodeIcon={false}
                onlyLeafCheckboxes={false}
                checkModel={'all'}
                icons={{
                    check: <IndicatorCheckbox checked={true} />,
                    uncheck: <IndicatorCheckbox checked={false} />,
                    halfCheck: (
                        <div className={css(S.HalfChecked)}>
                            <SubtractLine />
                        </div>
                    ),
                    expandClose: <AngleDown className={css(S.Collapse)} />,
                    expandOpen: <AngleUp className={css(S.Collapse)} />,
                }}
            />
        </div>
    );
};

const flatTreeValues = (
    treeNodes: AwardCheckboxTreeNode[],
    values: string[] = []
): string[] => {
    treeNodes.forEach((node) => {
        values.push(node.value);
        if (node.children) {
            flatTreeValues(node.children, values);
        }
    });
    return values;
};

export default AwardsFilterTree;
